import { createSelector } from '@reduxjs/toolkit';
import { selectLocationEntities } from '../slices/locationsSlice'; // Assuming locationsSlice exports this
import { selectMapBounds, selectMapZoom, selectAlgoliaSearchResults, selectSearchModeActive } from '../slices/mapUiSlice'; // Added Algolia selectors
import { selectCurrentMapLocationIds } from '../slices/customMapsSlice'; // Import selector from customMapsSlice
import type { FirestoreLocation, LocationType, AlgoliaLocation as AlgoliaLocationType } from '@/types/firestore'; // Ensure this type is correct, Added AlgoliaLocationType
import { createBoundsCache, createArrayCache } from '@/utils/selectorHelpers';

// Define the structure for the properties of a single point feature
// Expanded to include data needed by CustomMarker/SerializableMarkerLocation
interface PointProps {
  id: string;
  name: string;
  locationType: LocationType;
  address: string;
  averageRating?: number;
  ratingCount?: number;
  drinkCount?: number;
  lastSightingDate?: number; // Store as epoch milliseconds
  key?: string; // Add key property to match useSupercluster
}

// Define the MapPoint type based on the spec
type MapPoint = GeoJSON.Feature<GeoJSON.Point, PointProps>;

// Cache for GeoJSON point features and the FirestoreLocation/AlgoliaLocation they were generated from
interface CachedMapPointEntry {
  lastProcessedItem: FirestoreLocation | AlgoliaLocationType; // Store the item object reference
  feature: MapPoint;
  // Add specific property tracking for important fields
  lastLocationType: LocationType; // Track locationType explicitly for change detection
  // Potentially track other critical fields if they can change independently
}
const cachedPointMap = new Map<string, CachedMapPointEntry>();

// Create caches for stable references
const getStableBounds = createBoundsCache();
const getStablePointsArray = createArrayCache<MapPoint>();

// Selector for the map viewport state (bounds, zoom, and filters)
export const selectMapViewport = createSelector(
  [selectMapBounds, selectMapZoom /* Add filter selectors if needed */],
  (bounds, zoom /*, filters */) => {
    // TODO: Incorporate actual filter state from mapUiSlice if required by spec §4.3 filter comment
    const filters = {}; // Placeholder for filters
    // Use stable bounds reference
    const stableBounds = getStableBounds(bounds);
    
    // Return the viewport object. Reselect memoizes based on input stability.
    return { bounds: stableBounds, zoom, filters };
  }
);

// Selector to get visible GeoJSON points based on viewport and location entities
export const selectVisibleGeoJsonPoints = createSelector(
  [
    selectLocationEntities, 
    selectAlgoliaSearchResults, 
    selectSearchModeActive, 
    selectMapViewport,
    selectCurrentMapLocationIds
  ],
  (firestoreLocationEntities, algoliaResults, searchModeActive, { bounds }, currentMapLocationIds) => {
    if (!bounds) {
      return getStablePointsArray([]); // Ensure stable empty array reference
    }
    
    // Log for debugging
    console.log(`[mapSelectors] currentMapLocationIds length: ${currentMapLocationIds ? currentMapLocationIds.length : 0}`);
    
    // If we have map filtering, create a Set for O(1) lookups
    const mapLocationIdsSet = currentMapLocationIds && currentMapLocationIds.length > 0 
      ? new Set(currentMapLocationIds) 
      : null;

    const pts: MapPoint[] = [];
    const { west, south, east, north } = bounds;

    const sourceLocations = searchModeActive && algoliaResults ? algoliaResults : Object.values(firestoreLocationEntities).filter(Boolean);

    sourceLocations.forEach(loc => {
      if (!loc) return;

      let id: string, name: string, locType: LocationType, addr: string;
      let avgRating: number | undefined, ratingCnt: number | undefined, drnkCnt: number | undefined, lastSghtDate: number | undefined;
      let geoLat: number, geoLng: number;

      if ('objectID' in loc) { // AlgoliaLocationType
        if (!loc._geoloc || !loc.objectID) return;
        id = loc.objectID;
        name = loc.name ?? 'Unknown Location';
        locType = (loc.locationType || 'other') as LocationType;
        addr = loc.address ?? 'Address not available';
        avgRating = loc.averageRating;
        ratingCnt = loc.ratingCount;
        drnkCnt = loc.drinkCount;
        lastSghtDate = undefined; // Not typically in Algolia basic index
        geoLat = loc._geoloc.lat;
        geoLng = loc._geoloc.lng;
      } else { // FirestoreLocation
        if (!loc.coordinates || !loc.id) return;
        id = loc.id;
        name = loc.name ?? 'Unknown Location';
        locType = loc.locationType || 'other';
        addr = loc.address ?? 'Address not available';
        avgRating = loc.averageRating;
        ratingCnt = loc.ratingCount;
        drnkCnt = loc.drinkCount;
        lastSghtDate = typeof loc.lastSightingDate === 'number' ? loc.lastSightingDate : undefined;
        geoLat = loc.coordinates.latitude;
        geoLng = loc.coordinates.longitude;
      }

      // Apply custom map filter if active
      if (mapLocationIdsSet) {
        if (!mapLocationIdsSet.has(id)) {
          return; // Skip if not in the selected custom map
        }
        // If map filter is active, include all points regardless of bounds
      } else {
        // Apply bounds filter only if no custom map is active
        if (geoLat < south || geoLat > north || geoLng < west || geoLng > east) {
          return;
        }
      }
      
      // TODO: Apply other filters from 'filters' object

      const cacheKey = id;
      const cachedEntry = cachedPointMap.get(cacheKey);
      
      const shouldRecreateFeature = !cachedEntry || 
                                   cachedEntry.lastProcessedItem !== loc ||
                                   cachedEntry.lastLocationType !== locType;

      if (shouldRecreateFeature) {
        const newFeature: MapPoint = {
          type: 'Feature',
          properties: {
            id: id,
            name: name,
            locationType: locType,
            address: addr,
            averageRating: avgRating,
            ratingCount: ratingCnt,
            drinkCount: drnkCnt,
            lastSightingDate: lastSghtDate,
            key: `p-${id}`,
          },
          geometry: {
            type: 'Point',
            coordinates: [geoLng, geoLat],
          },
        };
        cachedPointMap.set(cacheKey, { 
          lastProcessedItem: loc as FirestoreLocation | AlgoliaLocationType, // Cast needed here 
          feature: newFeature,
          lastLocationType: locType 
        });
        pts.push(newFeature);
      } else {
        pts.push(cachedEntry.feature);
      }
    });
    
    return getStablePointsArray(pts);
  }
); 